import { BILLED_BY_USAGE_LABEL as ADMIN_BILLED_BY_USAGE_LABEL } from './meteredAdminCopy';

/**
 * The one charge-timing vocabulary for checkout money surfaces — the cart
 * summary sentence, the checkout summary rail chips, the mobile money
 * header and its panel, the payment-step charge-schedule block, and both
 * post-booking confirmation pages' per-booking Payment row all read these
 * constants rather than their own paraphrase, so a wording edit lands
 * once and the three groups a cart's lines fall into (charged today,
 * charged when the concierge confirms, quote to follow) can never drift
 * apart across surfaces.
 *
 * `CHARGED_TODAY_LABEL` / `CHARGED_ON_CONFIRM_LABEL` / `QUOTE_TO_FOLLOW_LABEL`
 * name the three groups in full-sentence contexts (the cart sentence, the
 * money header, the payment-step block). `CHARGED_ON_CONFIRM_CHIP_LABEL` is
 * the short uppercase-chip form of the on-confirm group used by the
 * checkout rail's per-line tags, alongside `CHARGED_TODAY_LABEL` and
 * `QUOTE_TO_FOLLOW_LABEL` reused verbatim as chip text.
 */

export const CHARGED_TODAY_LABEL = 'Charged today';
export const CHARGED_ON_CONFIRM_LABEL = 'Charged when your concierge confirms';
export const CHARGED_ON_CONFIRM_CHIP_LABEL = 'On confirm';
export const QUOTE_TO_FOLLOW_LABEL = 'Quote to follow';
// Re-exports the admin-authoring label (`constants/meteredAdminCopy.ts`) —
// the guest-facing disclosure and the admin option-row label name the same
// concept with the same word, so this re-export keeps the two from
// drifting apart rather than declaring the string a second time.
export const BILLED_BY_USAGE_LABEL = ADMIN_BILLED_BY_USAGE_LABEL;

// Shown in place of a gold $0.00 whenever a cart's charged-today figure is
// zero — a real charge amount is never rendered as a bare "$0".
export const NOTHING_CHARGED_TODAY_LABEL = 'Nothing charged today';

// Title for the payment-step card that replaces the itemised per-line
// charge-timing disclosure with the group totals from the shared money
// model.
export const CHARGE_SCHEDULE_TITLE = "When you'll be charged";

// Closing fine print on every surface that renders the group totals, for a
// cart that has at least one charged-today line — `chargeTimingFinePrint`
// (`utils/chargeTiming.ts`) selects between this and
// `CHARGE_ON_CONFIRM_FINE_PRINT` based on the cart's money model.
export const CHARGE_TIMING_FINE_PRINT =
  'Instantly confirmed stays are charged now. Everything else is charged ' +
  "only when your concierge confirms it — if they can't, nothing is " +
  'charged.';

// Closing fine print for a cart with no charged-today line — every line
// waits on the concierge's confirmation, so the instant-stay sentence above
// would be false for this cart.
export const CHARGE_ON_CONFIRM_FINE_PRINT =
  'Nothing is charged until your concierge confirms each item — if they ' +
  "can't, nothing is charged.";

// A metered line's estimate and cap render as two separate figures, never
// folded into one "up to" number — the estimate leads (it's what the
// guest will likely pay), the cap follows (the most they could pay).
// `ESTIMATE_LABEL` capitalises the word for group/row/panel contexts
// ("Est. $414.76 · max $622.14"); `ESTIMATE_LABEL_LOWER` is the same word
// lowercased for mid-sentence contexts (the cart sentence: "… est.
// $414.76 (max $622.14)"). `MAX_CHARGE_LABEL` always stays lowercase in
// both contexts.
export const ESTIMATE_LABEL = 'Est.';
export const ESTIMATE_LABEL_LOWER = 'est.';
export const MAX_CHARGE_LABEL = 'max';

// Sits beneath the desktop rail's h1 figure whenever the cart's headline
// is a metered estimate rather than a final charge.
export const ESTIMATED_SUBLABEL = 'estimated';

/** Renders the by-request line count appended to a headline total —
 *  "+ 1 awaiting quote" / "+ 2 awaiting quote". */
export const formatAwaitingQuoteCount = (awaitingQuoteCount: number): string =>
  `+ ${awaitingQuoteCount} awaiting quote`;

// The checkout page's own subheading sentence. A cart with no today line
// reads `CHECKOUT_INTRO_NOTHING_TODAY`; a cart with only a today line reads
// `CHECKOUT_INTRO_CHARGED_TODAY`; a cart mixing a today line with an
// on-confirm or quote line reads `CHARGE_TIMING_FINE_PRINT` instead of
// either single-group sentence, since neither alone describes it.
export const CHECKOUT_INTRO_CHARGED_TODAY =
  "Charged today — you'll enter your card in the payment step.";
export const CHECKOUT_INTRO_NOTHING_TODAY =
  "Nothing charged today — you're charged only when your concierge confirms.";

// The checkout page's "awaiting confirmation" notice heading for a cart
// with no by-request line — a priced line still needs a concierge to
// confirm it, even though nothing is unpriced. `QUOTE_TO_FOLLOW_LABEL`
// (above) is the heading once the cart has a by-request line instead.
export const AWAITING_CONFIRMATION_LABEL = 'Awaiting confirmation';

// Fallback sentence for the "Awaiting confirmation" notice's body when the
// cart has no by-request lines — a priced line still awaiting the
// concierge's confirmation before any charge.
export const CHARGED_ON_CONFIRM_NOTICE =
  'Charged when your concierge confirms — nothing is charged until then.';

// `ConfirmationPaymentRow`'s label and its two possible value fragments —
// composed with `CHARGED_ON_CONFIRM_LABEL` (above) for the on-confirm
// case, so the Payment row's wording can never drift from the cart/
// checkout surfaces' own "Charged when your concierge confirms" phrase.
export const PAYMENT_ROW_LABEL = 'Payment';
export const PAID_TODAY_LABEL = 'Paid today';
export const CARD_ON_FILE_LABEL = 'Card on file';
